import { TextField } from '@kerfjs/ui/text-field';
import { ToolbarControlGroup } from '@kerfjs/ui/toolbar-control-group';
import { ToolbarText } from '@kerfjs/ui/toolbar-text';
import { type SafeHtml } from 'kerfjs';

/**
 * A click-to-edit title for the main toolbar, shared by the demand and project-chat
 * titles (HS2-05350G). At rest it is Kerf's actionable `ToolbarText` (Kerf beta 72,
 * KF-PAGMJG): a native button directly in the leading zone that keeps its content
 * width when there is room and ellipsizes beside a status chip when space is tight
 * (HS2-K08YSC). Editing swaps in a Web Awesome input with a visually hidden label in a
 * borderless group that fills the zone. `hook` names the instance: the display
 * button's id is `<hook>-display`, and the input carries `data-title="<hook>"`, so the
 * app and tests can tell the titles apart while class values stay literal (KUI-L008).
 */
export function EditableToolbarTitle({
  label,
  hook,
  editing,
  value,
  display,
  placeholder,
  inputAction,
  editAction,
}: {
  label: string;
  hook: string;
  editing: boolean;
  value: string;
  /** The title as shown at rest (a placeholder when the title is empty). */
  display: string;
  placeholder?: string;
  inputAction: string;
  editAction: string;
}): SafeHtml {
  if (editing) {
    return (
      <ToolbarControlGroup label={label} appearance="borderless" className="editable-toolbar-title" sizing="fill">
        <TextField
          type="text"
          className="toolbar-title-input"
          size="compact"
          autofocus
          value={value}
          placeholder={placeholder}
          ariaLabel={label}
          rootAttributes={{ 'data-title': hook, 'data-action': inputAction }}
        />
      </ToolbarControlGroup>
    );
  }
  return <ToolbarText text={display} action={editAction} id={`${hook}-display`} className="editable-toolbar-title" />;
}
